import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@astro/api'
import { DateField, TimeField } from '@astro/date-field'
import {
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaTelegram,
  FaTiktok,
  FaSnapchat,
  FaPinterestP,
  FaSkype,
  FaXTwitter,
  FaLinkedinIn,
  FaGlobe,
} from 'react-icons/fa6'
import './ContactPage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import { useToast } from '../context/ToastContext'
import { useFaqs } from '../hooks/useFaqs'
import { useGeneralSettings } from '../hooks/useGeneralSettings'
import LuckyPicksWidget from '../components/tools/LuckyPicksWidget'
import { appUrl } from '@astro/config'
import { useAuth } from '@astro/auth'

/** Visible subject labels mapped onto the backend's `interested_in` enum
 * (general | astrology | shop | academy | other). */
const SUBJECTS = [
  { label: '--Select Option--', value: null },
  { label: 'General Enquiry', value: 'general' },
  { label: 'Astrology Enquiry', value: 'astrology' },
  { label: 'Shop Enquiry', value: 'shop' },
  { label: 'Academy Enquiry', value: 'academy' },
  { label: 'Something Else', value: 'other' },
]

const SOCIALS = [
  { label: 'Facebook', Icon: FaFacebookF },
  { label: 'Instagram', Icon: FaInstagram },
  { label: 'YouTube', Icon: FaYoutube },
]

/** Known platforms → brand icon; anything unrecognized gets a globe. */
const SOCIAL_ICONS = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  youtube: FaYoutube,
  telegram: FaTelegram,
  tiktok: FaTiktok,
  snapchat: FaSnapchat,
  pinterest: FaPinterestP,
  skype: FaSkype,
  twitter: FaXTwitter,
  x: FaXTwitter,
  linkedin: FaLinkedinIn,
}

/** Non-empty trimmed string, else '' — the API sends empty strings for unset fields. */
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** wa.me/tel need digits; bare 10-digit Indian numbers get the 91 prefix. */
const toDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}

export default function ContactPage() {
  const { showToast } = useToast()
  const { faqs } = useFaqs()
  const { settings } = useGeneralSettings()
  const { status } = useAuth()
  const isLoggedIn = status === 'authenticated'

  // API details with the page's previous copy as per-field fallback.
  const phone = val(settings.phone) || '+91 98765 43210'
  const phoneHref = `tel:+${toDigits(phone)}`
  const whatsappHref = `https://wa.me/${toDigits(val(settings.whatsapp) || phone)}`
  const email = val(settings.email) || 'info@astrorajumaharaj.com'
  const address = val(settings.address)
  const rawMap = val(settings.map_embed_url)
  const mapSrc =
    (rawMap.startsWith('<') ? (rawMap.match(/src="([^"]+)"/)?.[1] ?? '') : rawMap) ||
    'https://www.google.com/maps?q=Lajpat+Nagar,+New+Delhi&output=embed'

  const quickCards = [
    { as: 'a', href: phoneHref, icon: '📞', title: 'Call Us', line: phone, note: 'Mon–Sat · 9am–7pm', delay: 0 },
    { as: 'a', href: whatsappHref, target: '_blank', rel: 'noopener', icon: '💬', title: 'WhatsApp', line: 'Chat instantly', note: 'Fastest Response', delay: 0.05 },
    { as: 'a', href: `mailto:${email}`, icon: '✉️', title: 'Email Us', line: email, note: 'Reply in 24h', delay: 0.1 },
    { as: 'div', icon: '📍', title: 'Visit Us', line: address || 'New Delhi, India', note: 'By Appointment', delay: 0.15 },
  ]

  const infoItems = [
    {
      icon: '📍',
      title: 'Our Ashram',
      body: address || (
        <>
          Vedic Wisdom Bhawan, Lajpat Nagar,
          <br />
          New Delhi – 110024, India
        </>
      ),
    },
    { icon: '📞', title: 'Phone & WhatsApp', link: { href: phoneHref, text: phone } },
    { icon: '✉️', title: 'Email', link: { href: `mailto:${email}`, text: email } },
    { icon: '🕉️', title: 'Consultation Hours', body: 'Monday – Saturday · 9:00 AM – 7:00 PM IST' },
  ]

  // Socials from the API: direct platform fields plus the social_links list.
  const apiSocials = []
  const pushSocial = (label, url) => {
    const clean = val(url)
    if (clean) apiSocials.push({ label, url: clean })
  }
    ;['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'telegram', 'tiktok', 'snapchat', 'pinterest', 'skype'].forEach(
      (key) => pushSocial(key, settings[key]),
    )
    ; (Array.isArray(settings.social_links) ? settings.social_links : []).forEach((entry) => {
      if (typeof entry === 'string') pushSocial('website', entry)
      else if (entry) pushSocial(String(entry.platform || entry.name || entry.label || 'website').toLowerCase(), entry.url || entry.link)
    })

  const [submitting, setSubmitting] = useState(false);
  // Birth fields are controlled: the pickers display formatted text, so their
  // values have to come from state rather than being read off the DOM.
  const [birth, setBirth] = useState({ dob: '', tob: '' })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    const form = e.target
    const els = form.elements
    const name = els.name.value.trim()

    const payload = {
      full_name: name,
      phone: els.phone.value.trim(),
      email: els.email.value.trim(),
      interested_in: els.subject.value,
      message: els.message.value.trim(),
      consent: els.consent.checked,
    }
    // Optional birth fields — omitted entirely when blank so backend `date`/
    // `time` validation rules don't trip on empty strings.
    if (birth.dob) payload.date_of_birth = birth.dob
    if (birth.tob) payload.time_of_birth = birth.tob

    setSubmitting(true)
    try {
      await api.contact.submit(payload)
      showToast(`Thank you${name ? ', ' + name : ''}! Your message has reached the mission. 🙏`)
      form.reset()
      setBirth({ dob: '', tob: '' })
    } catch (err) {
      showToast(err?.message || 'Something went wrong — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageLayout className="contact-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO / BREADCRUMB BANNER ── */}
      <header className="relative pt-30 sm:pt-32.5 lg:pt-37.5 pb-20 lg:pb-28 overflow-hidden bg-[#5A0808] text-white text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/15 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.06] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-goldPrimary/40" />
            <span className="text-[9px] uppercase font-bold tracking-[0.4em] text-goldPrimary/70 font-royal">Reach The Mission</span>
            <span className="w-8 h-px bg-goldPrimary/40" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-royal text-[#FBF3E4] leading-tight">
            Contact{' '}
            <span className="bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">
              Maharaj Ji
            </span>
          </h1>
          <p className="text-sm sm:text-base text-white/50 font-light mt-5 max-w-xl mx-auto leading-relaxed">
            Have a question about a reading, a remedy, or a class? Reach out — our team responds to every seeker with
            care, usually within a few hours.
          </p>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-7 px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">Contact</span>
          </nav>
        </div>
      </header>

      {/* ── QUICK CONTACT CARDS ── */}
      <section className="relative py-16 lg:py-20 px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickCards.map((card) => {
              const Tag = card.as
              const linkProps =
                card.as === 'a'
                  ? { href: card.href, target: card.target, rel: card.rel }
                  : {}
              return (
                <Reveal
                  key={card.title}
                  as={Tag}
                  className={`val-card p-7 text-center${card.as === 'a' ? ' block' : ''}`}
                  style={{ transitionDelay: `${card.delay}s` }}
                  {...linkProps}
                >
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center text-2xl mb-4">
                    {card.icon}
                  </div>
                  <h3 className="text-base text-astroDark">{card.title}</h3>
                  <p className="text-[13px] text-stone-500 font-light mt-1.5">{card.line}</p>
                  <p className="text-[11px] text-amber-700/70 font-semibold uppercase tracking-wider mt-2">{card.note}</p>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── FORM + INFO ── */}
      <section className="contact-form-band relative py-16 lg:py-24 md:px-10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="contact-form-glow-a absolute -top-32 -left-32 w-[55vw] max-w-160 h-[55vw] max-h-160 rounded-full" />
          <div className="contact-form-glow-b absolute -bottom-32 -right-32 w-[45vw] max-w-135 h-[45vw] max-h-135 rounded-full" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: intro + info */}
            <Reveal className="lg:col-span-5 space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-7 h-px" style={{ background: 'rgba(245,158,11,.45)' }} />
                  <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em]" style={{ color: 'rgba(245,158,11,.58)' }}>
                    Send A Message
                  </span>
                </div>
                <h2 className="font-royal text-3xl lg:text-[40px] font-normal text-white leading-tight">
                  Let the Stars{' '}
                  <span className="bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">
                    Guide Your Path
                  </span>
                </h2>
                <p className="text-[14px] leading-[1.9] mt-4" style={{ color: 'rgba(255,255,255,.48)', fontWeight: 300 }}>
                  Whether you seek a Kundli analysis, a gemstone recommendation, or wish to enroll in our academy, share a
                  few details and our team will reach out personally.
                </p>
              </div>

              <div className="space-y-5">
                {infoItems.map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="contact-info-icon w-11 h-11 rounded-xl flex items-center justify-center text-lg shrink-0">
                      {item.icon}
                    </span>
                    <div>
                      <h4 className="text-sm text-white">{item.title}</h4>
                      {item.link ? (
                        <a
                          href={item.link.href}
                          className="text-[13px] text-white/45 font-light mt-0.5 leading-relaxed block hover:text-goldPrimary transition-colors"
                        >
                          {item.link.text}
                        </a>
                      ) : (
                        <p className="text-[13px] text-white/45 font-light mt-0.5 leading-relaxed">{item.body}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Socials */}
              <div className="pt-2">
                <p className="text-[9px] uppercase tracking-[.3em] font-semibold text-white/35 mb-3">Follow The Mission</p>
                <div className="flex gap-2.5 flex-wrap">
                  {apiSocials.length > 0
                    ? apiSocials.map((s) => {
                      const Icon = SOCIAL_ICONS[s.label] || FaGlobe
                      return (
                        <a
                          key={`${s.label}-${s.url}`}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={s.label}
                          className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/50 hover:text-goldPrimary hover:border-goldPrimary/40 transition-colors"
                        >
                          <Icon size={16} />
                        </a>
                      )
                    })
                    : SOCIALS.map((s) => (
                      <div
                        key={s.label}
                        aria-label={s.label}
                        className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/50 hover:text-goldPrimary hover:border-goldPrimary/40 transition-colors"
                      >
                        <s.Icon size={16} />
                      </div>
                    ))}
                </div>
              </div>
            </Reveal>

            {/* Right: form card */}
            <Reveal className="lg:col-span-7">
              <div className="bg-portalLight rounded-[26px] p-7 sm:p-10 shadow-2xl border border-goldPrimary/10">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="flbl" htmlFor="cf-name">Full Name *</label>
                      <input id="cf-name" name="name" type="text" required className="field" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="flbl" htmlFor="cf-phone">Phone Number *</label>
                      <input id="cf-phone" name="phone" type="tel" required className="field" placeholder="+91 …" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="flbl" htmlFor="cf-email">Email Address *</label>
                      <input id="cf-email" name="email" type="email" required className="field" placeholder="you@example.com" />
                    </div>
                    <div>
                      <label className="flbl" htmlFor="cf-subject">I'm Interested In</label>
                      <select id="cf-subject" name="subject" className="field">
                        {SUBJECTS.map((s) => (
                          <option key={s.label} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="flbl" htmlFor="cf-dob">Date of Birth</label>
                      <DateField id="cf-dob" name="dob" value={birth.dob} onChange={(e) => setBirth((b) => ({ ...b, dob: e.target.value }))} />
                    </div>
                    <div>
                      <label className="flbl" htmlFor="cf-tob">Time of Birth</label>
                      <TimeField id="cf-tob" name="tob" value={birth.tob} onChange={(e) => setBirth((b) => ({ ...b, tob: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="flbl" htmlFor="cf-msg">Your Message *</label>
                    <textarea
                      id="cf-msg"
                      name="message"
                      rows="5"
                      required
                      className="field"
                      placeholder="Tell us about your concern or what guidance you are seeking..."
                    />
                  </div>
                  <div className="flex items-start gap-2.5">
                    <input id="cf-consent" name="consent" type="checkbox" required className="mt-1 accent-goldPrimary w-4 h-4" />
                    <label htmlFor="cf-consent" className="text-[12px] text-stone-500 font-light leading-relaxed">
                      I consent to my details being used to respond to my enquiry. My information is kept private and
                      confidential.
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[12px] uppercase tracking-[.22em] px-8 py-4 rounded-full shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
                  >
                    {submitting ? 'Sending…' : 'Send Message ✦'}
                  </button>
                  <p className="text-center text-[11px] text-stone-400">
                    We typically respond within a few hours during consultation times.
                  </p>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── MAP ── */}
      <section className="relative md:px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <Reveal className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">Find Us</span>
            <h2 className="text-3xl lg:text-[40px] text-astroDark mt-3">Visit Our Ashram</h2>
            <p className="text-sm text-stone-500 font-light mt-4 leading-relaxed">
              In-person consultations are by appointment. Please call or message ahead to reserve your time with Maharaj
              Ji.
            </p>
          </Reveal>
          <Reveal
            className="rounded-[26px] overflow-hidden border border-goldPrimary/15 shadow-xl"
            style={{ boxShadow: '0 24px 60px -30px rgba(120,53,15,.4)' }}
          >
            <iframe
              title="Ashram location map"
              src={mapSrc}
              width="100%"
              height="420"
              style={{ border: 0, display: 'block', filter: 'saturate(.85)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      {faqs.length > 0 && (
        <section className="contact-faq-band relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/4 right-[-10%] w-[40vw] h-[40vw] rounded-full bg-goldGlow/10 blur-3xl" />
          </div>
          <div className="max-w-3xl mx-auto px-6 lg:px-12 relative z-10">
            <Reveal className="text-center mb-12">
              <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-goldPrimary/60">Before You Ask</span>
              <h2 className="text-3xl lg:text-[40px] text-white mt-3">Frequently Asked Questions</h2>
            </Reveal>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <Reveal
                  as="details"
                  key={faq.q}
                  className="faq-item bg-white/3 border border-white/10 rounded-2xl px-6 py-1"
                >
                  <summary className="flex items-center justify-between gap-4 py-4 text-white">
                    <span className="text-[15px] font-medium">{faq.q}</span>
                    <span className="faq-plus text-goldPrimary text-xl transition-transform shrink-0">+</span>
                  </summary>
                  <p className="text-[13px] text-white/45 font-light leading-relaxed pb-4">{faq.a}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA BAND ── */}
      <section className="contact-cta-band relative py-16 lg:py-20 md:px-10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" aria-hidden="true">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[200px] font-royal text-white select-none">ॐ</div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl lg:text-[42px] text-white leading-tight">Ready to Discover What the Stars Hold?</h2>
              <p className="text-sm text-white/70 font-light mt-4 max-w-xl mx-auto lg:mx-0">
                Book a personal consultation with Maharaj Ji and take the first step toward clarity and alignment.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mt-8">
                <a
                  href={appUrl('jyotish', "consultations")}
                  className="shine-btn bg-white text-astroDark font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full shadow-xl transition-transform hover:-translate-y-0.5"
                >
                  Book Consultation
                </a>
                {!isLoggedIn && (
                  <Link
                    to="/login"
                    className="border border-white/40 hover:border-white text-white font-semibold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all hover:bg-white/10"
                  >
                    Create Free Account
                  </Link>
                )}
              </div>
            </div>
            <LuckyPicksWidget />
          </div>
        </div>
      </section>

    </PageLayout>
  )
}
